import {
  latestAlertingChangelogEntriesQuery,
  latestChangelogEntriesIdsQuery,
  paginatedChangelogEntriesQuery,
} from './changelogQueries';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { sanityClient } from '../sanityClient';
import { ConsumerType } from '../consumer';
import type { UseQueryOptions } from '@tanstack/react-query/src/types';
import { ChangelogEntryDTO, ChangelogEntryIdDTO } from '@retail/changelog/types';
// eslint-disable-next-line @nx/enforce-module-boundaries
import type { OmitKeyof } from '@tanstack/query-core';

const mgApp = 'mgpris';

type Country = 'NO' | 'SE';

interface DefaultChangelogEntriesProps {
  language: string;
  country: Country;
  consumer: ConsumerType;
}

export const useLatestAlertingChangelogEntries = (
  sanityProps: DefaultChangelogEntriesProps,
  queryOptions?: OmitKeyof<UseQueryOptions<any>, 'initialData'>
) => {
  return useQuery<ChangelogEntryDTO[] | undefined>({
    queryKey: ['latestAlertingChangelogEntries', sanityProps],
    queryFn: () =>
      sanityClient.fetch(latestAlertingChangelogEntriesQuery, { ...sanityProps, mgApp }),
    suspense: false,
    ...queryOptions,
  });
};

export interface PaginatedChangelogEntriesProps extends DefaultChangelogEntriesProps {
  pageSize: number;
}

export const useInfiniteChangelogEntries = (
  sanityProps: PaginatedChangelogEntriesProps,
  options?: {
    enabled?: boolean;
  }
) => {
  const pageSize = sanityProps.pageSize;

  return useInfiniteQuery<ChangelogEntryDTO[], Error>({
    queryKey: ['changelogEntries', sanityProps],
    enabled: options?.enabled ?? true,
    queryFn: async ({ pageParam = 0 }) => {
      const offset = pageParam * pageSize;
      const end = offset + pageSize;
      return sanityClient.fetch(paginatedChangelogEntriesQuery, {
        ...sanityProps,
        offset,
        end,
        mgApp,
      });
    },
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage) return undefined;
      return lastPage.length === pageSize ? allPages.length : undefined;
    },
    suspense: false,
  });
};

interface ChangelogEntriesCountProps extends DefaultChangelogEntriesProps {
  maxAgeDays: number;
}

const millisPerDay = 86400_000;

export const useLatestChangelogEntriesIds = (
  sanityProps: ChangelogEntriesCountProps,
  queryOptions?: OmitKeyof<UseQueryOptions<any>, 'initialData'>
) => {
  const minChangeDate = new Date(Date.now() - sanityProps.maxAgeDays * millisPerDay).toISOString();

  return useQuery<ChangelogEntryIdDTO[] | undefined>({
    queryKey: ['changelogEntriesCount', sanityProps],
    queryFn: () =>
      sanityClient.fetch(latestChangelogEntriesIdsQuery, { ...sanityProps, minChangeDate, mgApp }),
    suspense: false,
    ...queryOptions,
  });
};
